import ReactHookFormInput from '@/components/ui/input/ReactHookFormInput';
import { Fleet, Vessel } from '@/types/api';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditFleetForm, EditFleetFormRequest } from '@/types/form';
import { useForm } from 'react-hook-form';
import ReactHookFormToggleSwitch from '@/components/ui/switch/ReactHookFormToogleSwitch';
import useVesselsStore from '@/store/vessels';
import { useMemo } from 'react';
import ReactHookFormCombobox from '@/components/ui/combobox/ReactHookFormCombobox';
import { useMutationCreateFleet, useMutationUpdateFleet } from '@/api/queries/fleet';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import Button from '@/components/ui/button';
import { OptionCombobox } from '@/components/ui/combobox';

type Props = {
  fleetData?: Fleet | null;
  closeForm: () => void;
};

export interface EditFleetFormComboBox extends EditFleetForm {
  vessel_ids: OptionCombobox[];
}

export default function FleetForm({ fleetData, closeForm }: Props) {
  const schema = Yup.object().shape({
    name: Yup.string().required('Fleet Name is Required'),
    is_active: Yup.boolean().required('Status is Required'),
    vessel_ids: Yup.array().min(1, 'Please select at least one vessel'),
  });

  const defaultValues: EditFleetFormComboBox = {
    name: fleetData?.name || '',
    is_active: fleetData?.is_active || false,
    vessel_ids:
      fleetData?.vessels?.map((val) => {
        return {
          id: val.id,
          value: val.id,
          label: val.vessel_name,
        };
      }) || [],
  };

  /***
   * Get vessels data
   */
  const vessels = useVesselsStore((state) => state.vessels);

  /**
   * Make memory cache for data vessels
   */
  const vesselsMemo = useMemo(() => {
    return vessels.map((vessel) => ({
      id: vessel.id,
      value: vessel.id,
      label: vessel.vessel_name,
    }));
  }, [vessels]);

  const {
    control,
    register,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<EditFleetFormComboBox>({
    //@ts-ignore
    defaultValues: defaultValues,
    mode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
  });

  const createFleet = useMutationCreateFleet();
  const updateFleet = useMutationUpdateFleet();

  const handleSaveAccount = async () => {
    try {
      const validate = await trigger();
      if (!validate) {
        return;
      }

      const formData: EditFleetFormComboBox = getValues();

      const formDataTransform: EditFleetFormRequest = {
        ...formData,
        vessel_ids: formData.vessel_ids.map((vessel: OptionCombobox) => vessel.value),
      };

      if (!fleetData) {
        const promiseSubmitData = new Promise(async (resolve, reject) => {
          try {
            const response = await createFleet.mutateAsync({
              params: formDataTransform,
            });
            if (response) {
              resolve(response);
              closeForm();
            }
          } catch (e) {
            reject(e);
          }
        });

        toast.promise(promiseSubmitData, {
          loading: 'Saving...',
          success: 'Successfully saved!',
          error: (err) => `Could not save. Please try again! :  ${err?.message}`,
        });
      } else {
        const promiseSubmitData = new Promise(async (resolve, reject) => {
          try {
            const response = await updateFleet.mutateAsync({
              id: fleetData.id,
              params: formDataTransform,
            });
            if (response) {
              resolve(response);
              closeForm();
            }
          } catch (e) {
            reject(e);
          }
        });

        toast.promise(promiseSubmitData, {
          loading: 'Saving...',
          success: 'Successfully saved!',
          error: (err) => `Could not save. Please try again! :  ${err?.message}`,
        });
      }
    } catch (e) {
      closeForm();
      console.log(e);
    }
  };

  return (
    <div className="space-y-8 p-10">
      <h1 className="text-2xl font-medium text-gray-800 dark:text-gray-200">
        {fleetData ? 'Edit Fleet' : 'Add New Fleet'}
      </h1>
      <div className="space-y-4">
        <div className="flex flex-col space-y-4">
          <ReactHookFormInput
            name="name"
            label="Fleet Name"
            control={control}
            errorMessage={errors?.name?.message}
          />

          <ReactHookFormCombobox
            name="vessel_ids"
            label="Vessel"
            options={vesselsMemo}
            valueMultiple={watch('vessel_ids')}
            control={control}
            onChangeMultiple={(val: OptionCombobox[]) => setValue('vessel_ids', val)}
            isMultiple={true}
            errorMessage={errors?.vessel_ids?.message}
          />

          <ReactHookFormToggleSwitch
            name="is_active"
            label="Activate Fleet (if true it will show in windows maps)"
            value={watch('is_active')}
            control={control}
            onChange={(val : any) => setValue('is_active', val)}
            errorMessage={errors?.is_active?.message}
          />
        </div>
        <div className="flex justify-between mt-20">
          <Button onClick={() => closeForm()} variant="outline">
            Cancel
          </Button>

          <Button
            onClick={() => handleSaveAccount()}
            className={clsx({
              'bg-gray-600': updateFleet.isPending || createFleet.isPending,
            })}
            disabled={updateFleet.isPending || createFleet.isPending}
          >
            Save Fleet
          </Button>
        </div>
      </div>
    </div>
  );
}
